import React from 'react';

// Servicio de autenticación
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';
const AUTH_KEYS = ['access_token', 'refresh_token', 'usuario'];

function getAuthStorage() {
  if (globalThis.localStorage !== undefined) {
    return globalThis.localStorage;
  }

  return {
    getItem: () => null,
    setItem: () => {},
    removeItem: () => {}
  };
}

function getLegacySessionStorage() {
  if (globalThis.sessionStorage !== undefined) {
    return globalThis.sessionStorage;
  }

  return null;
}

function readAuthValue(key) {
  const storage = getAuthStorage();
  const localValue = storage.getItem(key);
  if (localValue) {
    return localValue;
  }

  const legacyStorage = getLegacySessionStorage();
  const legacyValue = legacyStorage?.getItem(key);
  if (!legacyValue) {
    return null;
  }

  // Migra valores legacy para evitar sesiones partidas entre builds o previews.
  storage.setItem(key, legacyValue);
  legacyStorage.removeItem(key);
  return legacyValue;
}

function clearLegacySessionStorage() {
  const legacyStorage = getLegacySessionStorage();
  if (!legacyStorage) {
    return;
  }

  AUTH_KEYS.forEach((key) => legacyStorage.removeItem(key));
}

function saveAuthSession(data) {
  const storage = getAuthStorage();

  if (data.access_token) {
    storage.setItem('access_token', data.access_token);
  }
  if (data.refresh_token) {
    storage.setItem('refresh_token', data.refresh_token);
  }
  if (data.usuario) {
    storage.setItem('usuario', JSON.stringify(data.usuario));
  }
  clearLegacySessionStorage();
}

export const authService = {
  // Guardar tokens y sesión directamente (ej. tras registro de óptica trial)
  guardarTokens(access_token, refresh_token, usuario) {
    saveAuthSession({ access_token, refresh_token, usuario });
  },

  // Login
  async login(email, contraseña) {
    const response = await fetch(`${API_URL}/api/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, contraseña })
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Error al iniciar sesión');
    }

    const data = await response.json();
    saveAuthSession(data);
    return data;
  },

  // Registro
  async registro(email, nombre_completo, contraseña) {
    const response = await fetch(`${API_URL}/api/auth/registro`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, nombre_completo, contraseña })
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Error al registrarse');
    }

    const data = await response.json();
    saveAuthSession(data);
    return data;
  },

  // Renovar token usando refresh token
  async renovarToken() {
    const refresh_token = getAuthStorage().getItem('refresh_token');
    if (!refresh_token) {
      throw new Error('No hay refresh token disponible');
    }

    const response = await fetch(`${API_URL}/api/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refresh_token })
    });

    if (!response.ok) {
      const error = await response.json();
      // Si falla, limpiar tokens
      this.logout();
      throw new Error(error.detail || 'Error al renovar el token');
    }

    const data = await response.json();
    saveAuthSession({
      ...data,
      usuario: data.usuario || this.obtenerUsuarioActual()
    });
    return data;
  },

  // Obtener usuario actual
  obtenerUsuarioActual() {
    const usuario = readAuthValue('usuario');
    if (!usuario) {
      return null;
    }

    try {
      return JSON.parse(usuario);
    } catch {
      this.logout();
      return null;
    }
  },

  // Obtener token
  obtenerToken() {
    return readAuthValue('access_token');
  },

  // Obtener refresh token
  obtenerRefreshToken() {
    return readAuthValue('refresh_token');
  },

  // Verificar si está autenticado
  estaAutenticado() {
    return !!readAuthValue('access_token');
  },

  // Logout
  logout() {
    const storage = getAuthStorage();
    AUTH_KEYS.forEach((key) => storage.removeItem(key));
    clearLegacySessionStorage();
  },

  // Headers con autenticación
  obtenerHeaders() {
    const token = this.obtenerToken();
    return {
      'Content-Type': 'application/json',
      ...(token && { 'Authorization': `Bearer ${token}` })
    };
  }
};

// Hook para usar en componentes React
export function useAuth() {
  const [usuario, setUsuario] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [autenticado, setAutenticado] = React.useState(false);

  React.useEffect(() => {
    const usuarioActual = authService.obtenerUsuarioActual();
    if (usuarioActual) {
      setUsuario(usuarioActual);
      setAutenticado(true);
    }
    setLoading(false);
  }, []);

  const login = async (email, contraseña) => {
    const resultado = await authService.login(email, contraseña);
    setUsuario(resultado.usuario);
    setAutenticado(true);
    return resultado;
  };

  const logout = () => {
    authService.logout();
    setUsuario(null);
    setAutenticado(false);
  };

  return { usuario, loading, autenticado, login, logout };
}
